import React, { ReactNode, useRef, useState } from 'react';

import {
  Box,
  IconButton,
  Paper,
  Portal,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { domToPng } from 'modern-screenshot';
import { useConfirm } from 'material-ui-confirm';

import { Row } from '../../atoms/row';
import { ChartLegendChip } from '../../atoms/chart-legend-item';
import { Share } from '../../atoms/icons/share';
import { LogoLarge } from '../../atoms/logo-large';
import { useVisibility, VisibilityProps } from '../../store/use-visibility';
import { FormatNumberOpts } from '../../utils/format-number';
import { confirmYesNo } from '../../utils/use-confirm';

import { ReportPieChart, ReportPieChartItem } from './report-pie-chart';

export const defaultKwh: FormatNumberOpts = {
  unit: 'kilowatt-hour',
  style: 'unit',
  maximumFractionDigits: 1,
};

type CardProps = {
  title: ReactNode;
  icon?: ReactNode;
  pieChart?: ReportPieChartItem[];
  lines?: [ReactNode, ReactNode][];
  content?: ReactNode;
} & VisibilityProps;

export const Card = ({
  title,
  icon,
  pieChart,
  lines,
  content,
  visibility,
}: CardProps) => {
  const { share } = useCardShare({ title, pieChart });

  if (!useVisibility(visibility)) return null;

  return (
    <Box sx={{ p: { xs: 0, md: 1 }, pb: { xs: 2, md: 0 } }}>
      <Paper sx={{}}>
        <Row sx={{ gap: 1 }}>
          {icon && (
            <Box
              sx={{
                color: '#989898',
                '& svg': { fontSize: '1.5rem' },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {icon}
            </Box>
          )}
          <Typography variant="h3">{title}</Typography>

          {share}
        </Row>

        {pieChart && <CardPieChart data={pieChart} sx={{ mt: 2, mb: 5 }} />}

        {lines &&
          lines.map(([label, value], index) => (
            <Row
              key={index}
              sx={{
                justifyContent: 'space-between',
                mt: 1,
                borderBottom:
                  index !== lines.length - 1 ? '1px solid #ECECEC' : 'none',
                pt: 0.5,
                pb: 1,
              }}
            >
              <Typography color="textSecondary">{label}</Typography>
              <Typography variant="body2">{value}</Typography>
            </Row>
          ))}

        {content && <Box sx={{ mt: 2 }}>{content}</Box>}
      </Paper>
    </Box>
  );
};

type CardPieChartProps = {
  data: ReportPieChartItem[];
  sx?: SxProps;
  disableAnimation?: boolean;
  size?: 'small' | 'default';
  legendX?: ReactNode;
};

export const CardPieChart = ({
  data,
  sx = {},
  disableAnimation,
  size = 'default',
  legendX,
}: CardPieChartProps) => {
  return (
    <Row
      sx={{
        justifyContent: 'space-between',
        gap: 1,
        ...sx,
      }}
    >
      <Stack sx={{ gap: 1 }}>
        {legendX}
        {data
          .filter(
            ({ label, labelValue }) =>
              label && typeof labelValue !== 'undefined'
          )
          .map((item, index) => (
            <ChartLegendChip
              label={item.label}
              value={item.labelValue}
              color={item.color}
              key={index}
              disableLineBreak={size === 'small'}
            />
          ))}
      </Stack>

      <Box
        sx={{
          width: size === 'default' ? { xs: 140, sm: 200 } : 140,
          height: size === 'default' ? { xs: 140, sm: 200 } : 140,
        }}
      >
        <ReportPieChart
          data={data}
          disableAnimation={disableAnimation}
          size={size}
        />
      </Box>
    </Row>
  );
};

type UseCardShareOptions = {
  pieChart?: ReportPieChartItem[];
  title?: ReactNode;
};

export const useCardShare = ({ title, pieChart }: UseCardShareOptions) => {
  const confirm = useConfirm();

  const [contentMounted, setContentMounted] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const onShare = async () => {
    setContentMounted(true);

    const startTime = Date.now();
    while (!contentRef.current) {
      if (Date.now() - startTime > 1000) return;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    const dataUrl = await domToPng(contentRef.current as HTMLElement, {
      quality: 1,
      scale: 3,
    });

    setContentMounted(false);

    confirm({
      title: (
        <FormattedMessage
          id="reports.share.title"
          defaultMessage="Sdílet report"
        />
      ),
      titleProps: { variant: 'h2', sx: { textAlign: 'center' } },
      description: (
        <Stack>
          <Typography sx={{ textAlign: 'center', mb: 2 }}>
            <FormattedMessage
              id="reports.share.description"
              defaultMessage="Vybraný snímek můžete sdílet s ostatními."
            />
          </Typography>

          <Box
            component="img"
            src={dataUrl}
            sx={{
              width: '100%',
              height: 'auto',
            }}
          />
        </Stack>
      ),
      ...confirmYesNo,
      confirmationText: (
        <FormattedMessage
          id="reports.share.share"
          defaultMessage="Sdílet report"
        />
      ),
      confirmationButtonProps: {
        fullWidth: true,
        variant: 'contained',
        startIcon: <Share />,
      },
      hideCancelButton: true,
    }).then(
      async () => {
        if ('share' in navigator) {
          const base64Response = await fetch(dataUrl);
          const blob = await base64Response.blob();
          const file = new File([blob], 'statistics.png', {
            type: 'image/png',
          });

          await navigator.share({ files: [file] });
        } else {
          const link = document.createElement('a');
          link.download = 'statistics.png';
          link.href = dataUrl;
          link.click();
        }
      },
      () => {
        // noop
      }
    );
  };

  return {
    share: pieChart ? (
      <>
        <Tooltip
          title={
            <FormattedMessage
              id="reports.share_report"
              defaultMessage="Sdílet report"
            />
          }
        >
          <IconButton
            sx={{ marginLeft: 'auto' }}
            size="small"
            onClick={onShare}
          >
            <Share />
          </IconButton>
        </Tooltip>

        {contentMounted && (
          <Portal>
            <Box
              ref={contentRef}
              sx={{
                width: 420,
                p: 2,
                pb: 1,
                bgcolor: 'background.paper',
                borderRadius: 2,
                borderColor: 'primary.main',
                borderWidth: 1,
                borderStyle: 'solid',
                overflow: 'hidden',
              }}
            >
              <Typography variant="h3" sx={{ textTransform: 'capitalize' }}>
                {title}
              </Typography>
              <CardPieChart data={pieChart} disableAnimation size="small" />

              <LogoLarge
                sx={{
                  height: '30px !important',
                  width: '70px !important',
                  display: 'block',
                  mx: 'auto',
                }}
              />
            </Box>
          </Portal>
        )}
      </>
    ) : null,
  };
};
